import { describeMutationError } from "@api/mutationErrors";
import {
  DROPOFF_POSITIONS,
  EXAM_POSITIONS,
  POSITION_LABELS,
} from "@board/positions";
import { FieldError } from "@scribenote/brian-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type TodayVisit, visitsClient } from "./visitsClient";
import { visitsKeys } from "./visitsQueries";

export const VisitProgressControl = ({
  visit,
  label,
}: {
  visit: TodayVisit;
  /** Names the pet when the card holds more than one. */
  label?: string;
}) => {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (position: TodayVisit["position"]) =>
      visitsClient.setProgress(visit, position),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: visitsKeys.all }),
  });
  const positions = visit.flow === "exam" ? EXAM_POSITIONS : DROPOFF_POSITIONS;
  return (
    <div className="pointer-events-auto relative">
      <div className="flex items-center gap-2">
        {label && (
          <span className="shrink-0 text-[0.8125rem] text-annotation">
            {label}
          </span>
        )}
        <select
          aria-label={`Visit status for ${visit.patientName}`}
          value={visit.position}
          disabled={change.isPending || !!visit.cancelledAt}
          onChange={(event) =>
            change.mutate(event.target.value as TodayVisit["position"])
          }
          className="max-w-full rounded-paper border border-rule bg-card px-2 py-1 text-sm text-ink disabled:opacity-60"
        >
          {positions.map((position) => (
            <option key={position} value={position}>
              {POSITION_LABELS[position]}
            </option>
          ))}
        </select>
      </div>
      {change.isError && (
        <FieldError>
          {describeMutationError(change.error, "update the visit status")}
        </FieldError>
      )}
    </div>
  );
};
